import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ICaseReadReferralResponse,
  IPostReferralResponse,
  IReferralForm,
  IReferralFormResponse,
  IReferralLetter,
  IReferralState,
} from "../interfaces/IReferral";
import {
  uploadDocs,
  readOcrDoc,
  postReferralData,
  putReferralData,
} from "../apis/caseService";
import { fetchReferralById } from "../apis/patientService";

const initialState: IReferralState = {
  isReadingReferral: false,
  isSubmittingReferral: false,
  isSubmittingDocument: false,
  isFetchingReferral: false,
  error: "",
  referralData: {
    id: null,
    referralNo: "",
    referralExpiryFrom: "",
    referralExpiryTo: "",
    referralFor: null,
    referringDoctor: "",
    noOfVisits: null,
    ariAndSC: false,
    ipAllowed: false,
    referralType: null,
    cardCategory: null,
    cardNumber: "",
    serviceNumber: "",
    investigation: null,
    documentType: null,
    documentName: null,
    documentUrl: null,
    clinicalNotes: null,
    consultationFor: null,
    admission: null,
    beneficiaryId: "",
    age: null,
    gender: "",
    name: "",
    refferenceID: "",
    timeIssued: "",
    tokenNumber: "",
    dateIssued: "",
    services: null,
    referralServicesResponses: null,
    polyClinic: "",
    doctorName: "",
    referralBy: "",
    consumedVisits: "",
  },
  fetchCaseDetails: null,
  isAddCase: false,
  isAddVisit: false,
  addVisitResponse: null,
  isEditLoading: false,
  errorMessage: null,
  documentType: "",
  documentName: "",
  documentPath: "",
  isLoading: false,
};

export const uploadDocument = createAsyncThunk(
  "referral/uploadDocument",
  async ({
    formData,
    accessToken,
  }: {
    formData: FormData;
    accessToken: string;
  }) => {
    const response = await uploadDocs(formData, accessToken);
    return response;
  },
);

export const readReferralData = createAsyncThunk(
  "referral/readReferralData",
  async ({
    formData,
    accessToken,
  }: {
    formData: FormData;
    accessToken: string;
  }) => {
    const response = await readOcrDoc(formData, accessToken);
    return response;
  },
);

export const postSubmitReferral = createAsyncThunk(
  "referral/postSubmitReferral",
  async ({
    formData,
    accessToken,
    caseRegId,
  }: {
    formData: IReferralForm;
    accessToken: string;
    caseRegId: string;
  }) => {
    const response = await postReferralData(caseRegId, formData, accessToken);
    return response;
  },
);

export const UpdateSubmitReferral = createAsyncThunk(
  "referral/UpdateSubmitReferral",
  async ({
    caseId,
    formData,
    accessToken,
    caseRegId,
  }: {
    caseId: number | undefined;
    formData: IReferralForm;
    accessToken: string;
    caseRegId: number;
  }) => {
    const response = await putReferralData(caseId, formData, caseRegId);
    return response;
  },
);

export const getReferralById = createAsyncThunk(
  "referral/getReferralById",
  async (referralId: number) => {
    const response = await fetchReferralById(referralId);
    return response;
  },
);

const referralSlice = createSlice({
  name: "referral",
  initialState: initialState,
  reducers: {
    updateReferralData: (state, action: PayloadAction<IReferralLetter>) => {
      state.referralData = action.payload;
    },
    resetUpdateReferral: (state) => {
      state.referralData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadDocument.pending, (state) => {
        state.isSubmittingDocument = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state) => {
        state.isSubmittingDocument = false;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.isSubmittingDocument = false;
        state.isLoading = false;
        state.error = action.error.message || "Failed to post document";
      })
      .addCase(readReferralData.pending, (state) => {
        state.isReadingReferral = true;
        state.error = null;
        state.isLoading = true;
      })
      .addCase(
        readReferralData.fulfilled,
        (state, action: PayloadAction<ICaseReadReferralResponse>) => {
          state.isReadingReferral = false;
          state.isLoading = false;

          if (action.payload.referral_information) {
            state.referralData = {
              id: null,
              referralNo: action.payload.referral_information.referralNo || "",
              referralExpiryFrom:
                action.payload.referral_information.dateOfIssue || "",
              referralExpiryTo:
                action.payload.referral_information.validityUpto || "",
              referralFor: action.payload.referredTo || "",
              referringDoctor: "",
              noOfVisits: action.payload.referral_information.session || "",
              ariAndSC: false,
              ipAllowed: false,
              referralType:
                action.payload.referral_information.patientType || "",
              cardCategory:
                action.payload.patientAndEsmInformation.category || "",
              cardNumber: action.payload.patientAndEsmInformation.cardNo || "",
              serviceNumber:
                action.payload.patientAndEsmInformation.serviceNo || "",
              investigation:
                action.payload.provisionalDiagnosis?.investigation || "",
              clinicalNotes:
                action.payload.provisionalDiagnosis?.clinicalNotes || "",
              consultationFor:
                action.payload.provisionalDiagnosis?.consultationFor || "",
              admission: action.payload.provisionalDiagnosis?.admission || "",
              documentType: action.payload?.documentType,
              documentName: action.payload?.documentName,
              documentUrl: action.payload.documentPath,
              documentId: action.payload?.documentId,
              beneficiaryId: action.payload.beneficiaryId || "",
              age: action.payload.age || null,
              gender: action.payload.gender || "",
              name: action.payload.name || "",
              refferenceID: action.payload.refferenceID || "",
              timeIssued: action.payload.timeIssued || "",
              tokenNumber: action.payload.tokenNumber || "",
              dateIssued: action.payload.dateIssued || "",
              referralServicesResponses: null,
              services: null,
              polyClinic: action.payload.referralFromPolyclinic,
            };
          } else if (
            action.payload.beneficiaryId ||
            action.payload.refferenceID
          ) {
            state.referralData = {
              id: null,
              referralNo: "",
              referralExpiryFrom: "",
              referralExpiryTo: "",
              referralFor: "",
              referringDoctor: "",
              noOfVisits: "",
              ariAndSC: false,
              ipAllowed: false,
              referralType: "",
              cardCategory: "",
              cardNumber: "",
              serviceNumber: "",
              investigation: "",
              clinicalNotes: "",
              consultationFor: "",
              admission: "",
              documentType: action.payload.documentType,
              documentName: action.payload.documentName,
              documentUrl: action.payload.documentPath,
              beneficiaryId: action.payload.beneficiaryId || "",
              age: action.payload.age || null,
              gender: action.payload.gender || "",
              name: action.payload.name || "",
              refferenceID: action.payload.refferenceID || "",
              timeIssued: action.payload.timeIssued || "",
              tokenNumber: action.payload.tokenNumber || "",
              dateIssued: action.payload.dateIssued || "",
              referralServicesResponses: null,
              services: action.payload?.services || null,
              doctorName: action.payload?.doctorName || "",
              documentId: action.payload?.documentId || null,
            };
          } else {
            state.referralData = null;
          }
          state.error = null;
        },
      )
      .addCase(readReferralData.rejected, (state, action) => {
        state.isReadingReferral = false;
        state.error = action.error.message || "Failed to read referral";
        state.isLoading = false;
        state.referralData = null;
      })
      .addCase(postSubmitReferral.pending, (state) => {
        state.isSubmittingReferral = true;
        state.error = null;
        state.isLoading = true;
      })
      .addCase(
        postSubmitReferral.fulfilled,
        (state, action: PayloadAction<IPostReferralResponse>) => {
          state.isSubmittingReferral = false;
          state.referralData = {
            id:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.id ||
              action.payload?.updateReferralResponse?.cghsReferralResponse?.id,
            referralNo:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralNo,
            referralExpiryFrom:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralExpiryFrom,
            referralExpiryTo:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralExpiryTo,
            referralFor:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralFor,
            referringDoctor:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referringDoctor,
            noOfVisits:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.noOfVisits,
            ariAndSC:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.ariAndSC,
            polyClinic:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.polyClinic,
            ipAllowed:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.ipAllowed,
            referralType:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralType,
            cardCategory:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.cardCategory,
            cardNumber:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.cardNumber,
            serviceNumber:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.serviceNumber,
            investigation:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.investigation,
            clinicalNotes:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.clinicalNotes,
            consultationFor:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.consultationFor,
            admission:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.admission,
            documentType:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentType ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentType,
            documentName:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentName ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentName,
            documentUrl:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentUrl ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentUrl,
            documentId:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentId ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentId,
            beneficiaryId:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.beneficiaryId,
            age: action.payload?.updateReferralResponse?.cghsReferralResponse
              ?.age,
            dateIssued:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.dateIssued,
            gender:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.gender,
            name: action.payload?.updateReferralResponse?.cghsReferralResponse
              ?.name,
            refferenceID:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.refferenceID,
            tokenNumber:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.tokenNumber,
            timeIssued:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.timeIssued,
            referralServicesResponses: null,
            services:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.referralServicesResponses || [],
            referralBy:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.referralBy || "",
            remark:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.remark || "",
            consumedVisits:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.consumedVisits || "",
          };
          state.error = null;
          state.isLoading = false;
        },
      )
      .addCase(postSubmitReferral.rejected, (state, action) => {
        state.error = action.error.message || "Failed to post referral";
        state.isSubmittingReferral = false;
        state.isLoading = false;
        state.referralData = null;
      })
      .addCase(UpdateSubmitReferral.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(
        UpdateSubmitReferral.fulfilled,
        (state, action: PayloadAction<IPostReferralResponse>) => {
          state.referralData = {
            id:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.id ||
              action.payload?.updateReferralResponse?.cghsReferralResponse?.id,
            referralNo:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralNo,
            referralExpiryFrom:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralExpiryFrom,
            polyClinic:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.polyClinic,
            referralExpiryTo:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralExpiryTo,
            referralFor:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralFor,
            referringDoctor:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referringDoctor,
            noOfVisits:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.noOfVisits,
            ariAndSC:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.ariAndSC,
            ipAllowed:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.ipAllowed,
            referralType:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.referralType,
            cardCategory:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.cardCategory,
            cardNumber:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.cardNumber,
            serviceNumber:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.serviceNumber,
            investigation:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.investigation,
            clinicalNotes:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.clinicalNotes,
            consultationFor:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.consultationFor,
            admission:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.admission,
            documentType:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentType ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentType,
            documentName:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentName ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentName,
            documentUrl:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentUrl ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentUrl,
            documentId:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.documentId ||
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.documentId,
            beneficiaryId:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.beneficiaryId,
            age: action.payload?.updateReferralResponse?.cghsReferralResponse
              ?.age,
            dateIssued:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.dateIssued,
            gender:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.gender,
            name: action.payload?.updateReferralResponse?.cghsReferralResponse
              ?.name,
            refferenceID:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.refferenceID,
            tokenNumber:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.tokenNumber,
            timeIssued:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.timeIssued,
            referralServicesResponses: null,
            services:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.referralServicesResponses || [],
            referralBy:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.referralBy || "",
            remark:
              action.payload?.updateReferralResponse?.cghsReferralResponse
                ?.remark || "",
            consumedVisits:
              action.payload?.updateReferralResponse?.echsReferralResponse
                ?.consumedVisits || "",
          };
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(UpdateSubmitReferral.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to post referral";
        state.referralData = null;
      })
      .addCase(getReferralById.pending, (state) => {
        state.isFetchingReferral = true;
        state.error = null;
        state.isLoading = true;
      })
      .addCase(
        getReferralById.fulfilled,
        (state, action: PayloadAction<IReferralFormResponse>) => {
          state.isFetchingReferral = false;
          state.isLoading = false;

          if (
            action.payload?.echsReferralResponse?.id ||
            action.payload?.cghsReferralResponse?.id
          ) {
            state.referralData = {
              id:
                action.payload?.echsReferralResponse?.id ||
                action.payload?.cghsReferralResponse?.id,
              referralNo: action.payload?.echsReferralResponse?.referralNo,
              referralExpiryFrom:
                action.payload?.echsReferralResponse?.referralExpiryFrom,
              referralExpiryTo:
                action.payload?.echsReferralResponse?.referralExpiryTo,
              referralFor: action.payload?.echsReferralResponse?.referralFor,
              referringDoctor:
                action.payload?.echsReferralResponse?.referringDoctor,
              noOfVisits: action.payload?.echsReferralResponse?.noOfVisits,
              ariAndSC: action.payload?.echsReferralResponse?.ariAndSC,
              ipAllowed: action.payload?.echsReferralResponse?.ipAllowed,
              referralType: action.payload?.echsReferralResponse?.referralType,
              cardCategory: action.payload?.echsReferralResponse?.cardCategory,
              cardNumber: action.payload?.echsReferralResponse?.cardNumber,
              polyClinic: action.payload?.echsReferralResponse?.polyClinic,
              serviceNumber:
                action.payload?.echsReferralResponse?.serviceNumber,
              investigation:
                action.payload?.echsReferralResponse?.investigation,
              clinicalNotes:
                action.payload?.echsReferralResponse?.clinicalNotes,
              consultationFor:
                action.payload?.echsReferralResponse?.consultationFor,
              admission: action.payload?.echsReferralResponse?.admission,
              documentType:
                action.payload?.echsReferralResponse?.documentType ||
                action.payload?.cghsReferralResponse?.documentType,
              documentName:
                action.payload?.echsReferralResponse?.documentName ||
                action.payload?.cghsReferralResponse?.documentName,
              documentUrl:
                action.payload?.echsReferralResponse?.documentUrl ||
                action.payload?.cghsReferralResponse?.documentUrl,
              documentLabelType:
                action.payload?.echsReferralResponse?.documentLabelType ||
                action.payload?.cghsReferralResponse?.documentLabelType,
              documentId:
                action.payload?.echsReferralResponse?.documentId ||
                action.payload?.cghsReferralResponse?.documentId,
              beneficiaryId:
                action.payload?.cghsReferralResponse?.beneficiaryId,
              age: action.payload?.cghsReferralResponse?.age,
              dateIssued: action.payload?.cghsReferralResponse?.dateIssued,
              gender: action.payload?.cghsReferralResponse?.gender,
              name: action.payload?.cghsReferralResponse?.name,
              refferenceID: action.payload?.cghsReferralResponse?.refferenceID,
              tokenNumber: action.payload?.cghsReferralResponse?.tokenNumber,
              timeIssued: action.payload?.cghsReferralResponse?.timeIssued,
              referralServicesResponses: null,
              services:
                action.payload?.cghsReferralResponse
                  ?.referralServicesResponses || [],
              referralBy:
                action.payload?.cghsReferralResponse?.referralBy || "",
              remark: action.payload?.cghsReferralResponse?.remark || "",
              consumedVisits:
                action.payload?.echsReferralResponse?.consumedVisits || "",
            };
          } else {
            state.referralData = null;
          }
          state.error = null;
        },
      )
      .addCase(getReferralById.rejected, (state, action) => {
        state.isFetchingReferral = false;
        state.error = action.error.message || "Failed to read referral";
        state.isLoading = false;
        state.referralData = null;
      });
  },
});

export const { updateReferralData, resetUpdateReferral } =
  referralSlice.actions;
export default referralSlice.reducer;
